import { SubmissionError } from 'redux-form';
import trans from '../Translations/translation.service';

const CAKE_MESSAGE_KEYS = {
  'This value is already in use': 'default.forms.validations.isUnique',
  'This value does not exist': 'default.forms.validations.existsIn',
};

const RULE_MESSAGE_KEYS = {
  _isUnique: 'default.forms.validations.isUnique',
  _existsIn: 'default.forms.validations.existsIn',
};

export const translateApiErrorMessage = (message, ruleKey) => {
  if (ruleKey && RULE_MESSAGE_KEYS[ruleKey]) {
    return trans(RULE_MESSAGE_KEYS[ruleKey]);
  }

  if (typeof message === 'string' && CAKE_MESSAGE_KEYS[message]) {
    return trans(CAKE_MESSAGE_KEYS[message]);
  }

  return typeof message === 'string' ? message : '';
};

export const getFieldFirstError = errors => {
  if (typeof errors === 'string') {
    return translateApiErrorMessage(errors);
  }

  if (typeof errors !== 'object' || !errors) {
    return '';
  }

  if (Array.isArray(errors)) {
    return getFieldFirstError(errors[0]);
  }

  const keys = Object.keys(errors);
  if (!keys.length) {
    return '';
  }

  const ruleKey = keys[0];
  const error = errors[ruleKey];

  if (typeof error === 'string') {
    return translateApiErrorMessage(error, ruleKey);
  }

  return getFieldFirstError(error);
};

export const parseApiItemErrors = apiErrors => {
  const errors = {};

  if (Array.isArray(apiErrors)) {
    const first = apiErrors.find(item => item && typeof item === 'object' && !Array.isArray(item));
    return first ? parseApiItemErrors(first) : errors;
  }

  if (typeof apiErrors !== 'object' || !apiErrors) {
    return errors;
  }

  Object.keys(apiErrors).forEach(field => {
    const value = apiErrors[field];
    errors[field] = typeof value === 'string'
      ? translateApiErrorMessage(value)
      : getFieldFirstError(value);
  });

  return errors;
};

export const parseApiArrayErrors = (apiErrors, field) => {
  const errors = { [field]: [] };

  if (!Array.isArray(apiErrors)) {
    return errors;
  }

  apiErrors.forEach((itemErrors, index) => {
    const hasErrors = !Array.isArray(itemErrors);

    if (hasErrors) {
      errors[field][index] = parseApiItemErrors(itemErrors);
    }
  });

  return errors;
};

const fieldFromJsonApiPointer = pointer => {
  if (typeof pointer !== 'string' || !pointer) {
    return '_error';
  }

  const parts = pointer.split('/').filter(Boolean);
  return parts[parts.length - 1] || '_error';
};

const parseJsonApiErrors = jsonApiErrors => {
  const errors = {};

  jsonApiErrors.forEach(item => {
    if (!item || typeof item !== 'object') {
      return;
    }

    const field = fieldFromJsonApiPointer(item.source && item.source.pointer);
    const message = translateApiErrorMessage(
      item.detail || item.title || '',
      item.title && item.title.startsWith('_') ? item.title : undefined,
    );

    if (message && !errors[field]) {
      errors[field] = message;
    }
  });

  return errors;
};

export const extractResponseErrors = response => {
  if (typeof response !== 'object' || !response) {
    return null;
  }

  if (response.data && response.data.errors) {
    return response.data.errors;
  }

  if (Array.isArray(response.errors)) {
    return parseJsonApiErrors(response.errors);
  }

  if (response.errors && typeof response.errors === 'object') {
    return response.errors;
  }

  return null;
};

const fallbackApiErrorMessage = body => {
  if (body && body.data && typeof body.data.message === 'string') {
    return body.data.message;
  }

  if (body && typeof body.message === 'string') {
    return body.message;
  }

  return trans('default.forms.validations.unprocessable');
};

export const catchApiItemErrors = err => {
  const raw = extractResponseErrors(err && err.body);
  const errors = parseApiItemErrors(raw);
  const hasFieldErrors = Object.keys(errors).some(key => key !== '_error' && errors[key]);

  if (!hasFieldErrors) {
    errors._error = errors._error || fallbackApiErrorMessage(err && err.body);
  }

  throw new SubmissionError(errors);
};

export const catchApiArrayErrors = field => err => {
  const raw = extractResponseErrors(err && err.body);
  const errors = parseApiArrayErrors(raw, field);
  const hasItemErrors = Array.isArray(errors[field]) && errors[field].some(Boolean);

  if (!hasItemErrors) {
    throw new SubmissionError({
      _error: fallbackApiErrorMessage(err && err.body),
    });
  }

  throw new SubmissionError(errors);
};
